import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import Avatar, { AvatarItem, type AvatarItemProps } from '@atlaskit/avatar';
import CalendarFilledIcon from '@atlaskit/icon/core/migration/calendar--calendar-filled';
import GraphLineIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import PeopleIcon from '@atlaskit/icon/core/migration/people-group--people';
import { components, type OptionProps } from '@atlaskit/select';

import { messages } from './messages';
import { type AutoCompleteWithNoOneItem } from './types';

const CustomOption = <IsMulti extends boolean = false>(
	props: OptionProps<AutoCompleteWithNoOneItem, IsMulti>,
) => {
	const { data } = props;

	const intlApi = useIntl();

	const responderOption: AvatarItemProps = useMemo(() => {
		let defaultConfig = {
			primaryText: data.label,
		};
		switch (data.type) {
			case 'user':
				return {
					...defaultConfig,
					avatar: <Avatar size="small" src={data.avatarUrl} />,
					secondaryText: data.email,
				};
			case 'escalation':
				return {
					...defaultConfig,
					avatar: (
						<GraphLineIcon
							color="currentColor"
							spacing="spacious"
							label={intlApi.formatMessage(messages.escalation)}
						/>
					),
				};
			case 'schedule':
				return {
					...defaultConfig,
					avatar: (
						<CalendarFilledIcon
							color="currentColor"
							spacing="spacious"
							label={intlApi.formatMessage(messages.schedule)}
						/>
					),
				};
			case 'noone':
				return {
					...defaultConfig,
					avatar: null,
				};
			case 'team':
				return {
					...defaultConfig,
					avatar: data.avatarUrl ? (
						<Avatar size="small" src={data.avatarUrl} />
					) : (
						<PeopleIcon
							color="currentColor"
							spacing="spacious"
							label={intlApi.formatMessage(messages.team)}
						/>
					),
				};
			default:
				throw new Error('Incorrect data type is provided or it is missing!');
		}
	}, [data.avatarUrl, data.email, data.label, data.type, intlApi]);

	return (
		<components.Option {...props}>
			<AvatarItem
				avatar={responderOption.avatar}
				primaryText={responderOption.primaryText}
				secondaryText={responderOption.secondaryText}
			/>
		</components.Option>
	);
};

export { CustomOption };
